/**
 * Порт split_entities из TDLib (td/telegram/MessageEntity.cpp). §Хребет данных 3.3.
 *
 * Зачем он вообще нужен: после resplit сущности образуют корректный лес — любые две либо
 * вложены, либо не пересекаются, — и предусловие buildTree становится ИСТИННЫМ.
 * Вариант «слитые максимальные интервалы на тип» его нарушает и потому отвергнут.
 *
 * Два свойства, которые надо понимать, чтобы не «упростить» алгоритм обратно:
 *
 *   1. Соседние однотипные сущности сливаются, но НЕ ВСЕГДА.
 *      {italic 0,2}+{italic 2,1} → {italic 0,3}.
 *      А {bold 0,5}+{italic 2,5}+{underline 4,5} даёт
 *      {bold 0,2}{bold 2,2}{italic 2,2}{bold 4,1}{italic 4,3}{underline 4,5} —
 *      два соседних bold НЕ сливаются, потому что слияние разорвало бы вложенность с italic.
 *      Ровно ради этого в TDLib стоит комментарий «must be called once to not merge some
 *      adjacent entities».
 *
 *   2. Splittable внутри pre/code не режется, а ИСЧЕЗАЕТ (ветка result.resize(old_size)
 *      в оригинале). У нас исчезнувшие куски возвращаются отдельным списком dropped_in_pre
 *      и превращаются в fatal — а не в тихую потерю форматирования.
 */

import type { MessageEntity } from './draft.ts';
import { sortEntities } from './entity.ts';

export const SPLITTABLE = ['bold', 'italic', 'underline', 'strikethrough', 'spoiler'] as const;
export type SplittableType = (typeof SPLITTABLE)[number];

/** Порядок фиксирован: он задаёт порядок выдачи внутри flush(). */
const SPLITTABLE_INDEX: Readonly<Record<string, number>> = {
  bold: 0,
  italic: 1,
  underline: 2,
  strikethrough: 3,
  spoiler: 4,
};

export function isSplittable(t: string): t is SplittableType {
  return SPLITTABLE_INDEX[t] !== undefined;
}

/** Типы, внутри которых форматирование не живёт: сервер его выбрасывает. */
const CODE_LIKE: ReadonlySet<string> = new Set(['code', 'pre']);

const END_OF_TEXT = 0x7fffffff;

type Mutable = { type: string; offset: number; length: number };

function make(type: string, offset: number, length: number): MessageEntity {
  return { type, offset, length } as unknown as MessageEntity;
}

export interface ResplitResult {
  entities: MessageEntity[];
  /** Куски splittable-сущностей, съеденные pre/code. Непустой список — fatal у вызывающего. */
  dropped_in_pre: MessageEntity[];
}

/**
 * Вход — ВСЕ явные сущности черновика. Автоопределённые сюда не попадают никогда
 * (§Хребет данных 3.5): иначе каноническая форма зависела бы от версии TDLib на серверах
 * Telegram.
 *
 * others (code, pre, text_link, blockquote, expandable_blockquote) обязаны быть уже
 * проверены на непересечение шагами E2–E4 canonicalize().
 */
export function resplitEntities(all: readonly MessageEntity[]): ResplitResult {
  const splittable = sortEntities(all.filter((e) => isSplittable(e.type))) as unknown as Mutable[];
  const others = sortEntities(all.filter((e) => !isSplittable(e.type)));

  const begin = new Int32Array(5);
  const end = new Int32Array(5);
  const result: MessageEntity[] = [];
  const dropped_in_pre: MessageEntity[] = [];

  const flush = (offset: number): void => {
    for (let i = 0; i < 5; i++) {
      const e = end[i] as number;
      const b = begin[i] as number;
      if (e !== 0 && b < offset) {
        const type = SPLITTABLE[i] as string;
        if (e <= offset) {
          result.push(make(type, b, e - b));
          begin[i] = 0;
          end[i] = 0;
        } else {
          result.push(make(type, b, offset - b));
          begin[i] = offset;
        }
      }
    }
  };

  let consumed = 0;
  const addEntities = (endOffset: number): void => {
    while (consumed < splittable.length) {
      const e = splittable[consumed] as Mutable;
      if (e.offset >= endOffset) break;
      const i = SPLITTABLE_INDEX[e.type] as number;
      if ((end[i] as number) !== 0 && e.offset <= (end[i] as number)) {
        // Слияние с открытым пробегом того же типа.
        end[i] = Math.max(end[i] as number, e.offset + e.length);
      } else {
        flush(e.offset);
        begin[i] = e.offset;
        end[i] = e.offset + e.length;
      }
      consumed++;
    }
    flush(endOffset);
  };

  const stack: MessageEntity[] = [];
  const addOffset = (offset: number): void => {
    while (stack.length > 0) {
      const top = stack[stack.length - 1] as MessageEntity;
      const topEnd = top.offset + top.length;
      if (offset < topEnd) break;
      const mark = result.length;
      addEntities(topEnd);
      if (CODE_LIKE.has(top.type)) {
        // Всё, что нарезалось внутри pre/code, сервер бы выбросил молча.
        for (const cut of result.splice(mark)) dropped_in_pre.push(cut);
      }
      stack.pop();
    }
    addEntities(offset);
  };

  for (const o of others) {
    addOffset(o.offset);
    stack.push(o);
  }
  addOffset(END_OF_TEXT);

  return { entities: sortEntities([...result, ...others]), dropped_in_pre };
}
